/**
 * FeaturesPageShowcase — scripted product demo for the Features page.
 *
 * 8 feature stories, each with two frames:
 *   Frame A (list/overview)  →  cursor clicks a specific row/element
 *   Frame B (detail/drilldown) →  cursor interacts with a feature-specific element
 *
 * Layout geometry (used for precise cursor positions):
 *   Showcase total:  1024 px wide × 550 px tall (lg breakpoint)
 *   Sidebar:         220 px wide  → sidebar item centre x ≈ 11%
 *   Topbar:           ~40 px tall  → frame content starts at y=7.3%
 *   Frame height:    550 - 40 = 510 px
 */

import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Building2, Briefcase, GitMerge, Video, Target,
  ShieldCheck, Zap, BarChart2, Bell, Search, ChevronRight,
  ArrowLeft, Check, AlertTriangle, Radio, Download, Plus,
  Edit2, Mail, TrendingUp, ToggleLeft, ToggleRight,
} from 'lucide-react';

/* ─── cursor overlay ─────────────────────────────────────────────────────── */

function CursorOverlay({ pos, clicking, ripple }: {
  pos: { x: number; y: number };
  clicking: boolean;
  ripple: boolean;
}) {
  return (
    <div className="pointer-events-none absolute inset-0 z-50 overflow-hidden rounded-[inherit]">
      <motion.div
        className="absolute"
        animate={{ left: `${pos.x}%`, top: `${pos.y}%` }}
        transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
      >
        <motion.div animate={{ scale: clicking ? 0.75 : 1 }} transition={{ duration: 0.08 }}>
          <svg width="18" height="22" viewBox="0 0 18 22" fill="none"
            className="drop-shadow-[0_2px_6px_rgba(0,0,0,0.5)]">
            <path d="M2 2L2 16L5.5 12.5L8.5 19.5L10.5 18.5L7.5 11.5L13 11.5Z"
              fill="white" stroke="#1a1a2e" strokeWidth="1.3"
              strokeLinejoin="round" strokeLinecap="round" />
          </svg>
        </motion.div>
        <AnimatePresence>
          {ripple && (
            <motion.div key="r"
              className="absolute -left-5 -top-5 h-10 w-10 rounded-full border-[2.5px] border-violet-400/90"
              initial={{ scale: 0.1, opacity: 1 }}
              animate={{ scale: 2.6, opacity: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.38, ease: 'easeOut' }}
            />
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}

/* ─── sidebar ────────────────────────────────────────────────────────────── */

const NAV_GROUPS = [
  { label: 'DATA', items: [
    { id: 'candidates',     label: 'Candidates',      Icon: Users },
    { id: 'clients',        label: 'Clients',          Icon: Building2 },
    { id: 'jobs',           label: 'Jobs',             Icon: Briefcase },
  ]},
  { label: 'PIPELINE', items: [
    { id: 'applications',   label: 'Applications',    Icon: GitMerge },
    { id: 'aiInterviews',   label: 'AI Interviews',   Icon: Video },
  ]},
  { label: 'ROLEFIT AI', items: [
    { id: 'precisionMatch', label: 'Precision match', Icon: Target },
  ]},
  { label: 'ACCESS', items: [
    { id: 'rbac',           label: 'Manage rights',   Icon: ShieldCheck },
  ]},
  { label: 'AUTOMATIONS', items: [
    { id: 'workflows',      label: 'Workflows',        Icon: Zap },
  ]},
  { label: 'INSIGHTS', items: [
    { id: 'analytics',      label: 'Analytics',        Icon: BarChart2 },
  ]},
];

function Sidebar({ activeId }: { activeId: string }) {
  return (
    <aside className="flex w-[220px] shrink-0 flex-col border-r border-[#e5e5e5] bg-[#f3f3f3]">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] bg-white/80 px-3 py-3">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-[#8E86FF]">
          <span className="text-[11px] font-black text-white">G</span>
        </div>
        <span className="text-sm font-extrabold text-zinc-900">GoGrabJob</span>
        <span className="ml-auto rounded-md bg-[rgba(142,134,255,0.12)] px-1.5 py-0.5 text-[9px] font-bold text-[#7B6EE8] ring-1 ring-[rgba(142,134,255,0.35)]">
          Business
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-3 py-2">
        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#8E86FF] text-[11px] font-bold text-white">N</div>
        <div className="min-w-0">
          <p className="truncate text-xs font-semibold text-zinc-900">Neha</p>
          <p className="truncate text-[10px] text-zinc-400">Talent · Recruiting</p>
        </div>
      </div>
      <nav className="flex-1 overflow-hidden px-2 py-2">
        {NAV_GROUPS.map(({ label, items }) => (
          <div key={label} className="mb-1.5">
            <p className="mb-0.5 px-2 text-[9px] font-bold uppercase tracking-wider text-zinc-400">{label}</p>
            {items.map(({ id, label: lbl, Icon }) => {
              const active = id === activeId;
              return (
                <div key={id}
                  className="flex items-center gap-2 rounded-lg px-2 py-1.5 text-xs font-medium"
                  style={{ background: active ? 'rgba(142,134,255,0.12)' : 'transparent', color: active ? '#7B6EE8' : '#374151' }}>
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  {lbl}
                  {id === 'aiInterviews' && (
                    <span className="ml-auto rounded-sm bg-emerald-100 px-1 py-px text-[8px] font-bold uppercase text-emerald-600">Live</span>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </nav>
      <div className="shrink-0 border-t border-[#e5e5e5] px-3 py-2">
        <p className="text-[9px] text-zinc-400">Simulation · Trial workspace</p>
      </div>
    </aside>
  );
}

/* ─── topbar ─────────────────────────────────────────────────────────────── */

function Topbar({ crumbs }: { crumbs: string[] }) {
  return (
    <div className="flex shrink-0 items-center gap-3 border-b border-[#e5e5e5] bg-white px-4 py-2">
      <div className="flex items-center gap-1 text-[11px] text-zinc-400">
        {crumbs.map((c, i) => (
          <span key={c} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3" />}
            <span className={i === crumbs.length - 1 ? 'font-semibold text-zinc-800' : ''}>{c}</span>
          </span>
        ))}
      </div>
      <div className="ml-auto flex items-center gap-2">
        <div className="flex items-center gap-1.5 rounded-lg border border-[#e5e5e5] bg-[#f9fafb] px-3 py-1 text-[10px] text-zinc-400">
          <Search className="h-3 w-3" />Search…
        </div>
        <span className="text-[10px] text-zinc-400">Help</span>
        <div className="flex h-5 items-center rounded bg-[#8E86FF] px-1.5 text-[9px] font-bold text-white">Agent</div>
        <Bell className="h-3.5 w-3.5 text-zinc-400" />
      </div>
    </div>
  );
}

/* ─── shared primitives ──────────────────────────────────────────────────── */

function Score({ v }: { v: number }) {
  const cls = v >= 85 ? 'bg-emerald-50 text-emerald-700 ring-emerald-200'
    : v >= 70 ? 'bg-violet-50 text-violet-700 ring-violet-200'
    : 'bg-amber-50 text-amber-700 ring-amber-200';
  return <span className={`inline-flex items-center rounded-full px-1.5 py-0.5 text-[10px] font-semibold ring-1 ${cls}`}>{v}%</span>;
}

function StagePill({ s }: { s: string }) {
  const cls = s === 'Offer' ? 'bg-emerald-50 text-emerald-700'
    : s === 'Interview' ? 'bg-violet-50 text-violet-700'
    : s === 'Screening' ? 'bg-blue-50 text-blue-700'
    : 'bg-zinc-100 text-zinc-500';
  return <span className={`rounded-md px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{s}</span>;
}

/** grid= CSS grid-template-columns string for TRow/THead */
function THead({ cols, grid }: { cols: string[]; grid?: string }) {
  return (
    <div className="grid shrink-0 border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-2"
      style={{ gridTemplateColumns: grid ?? `repeat(${cols.length}, 1fr)` }}>
      {cols.map(c => (
        <span key={c} className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{c}</span>
      ))}
    </div>
  );
}

function TRow({ grid, highlight, children }: { grid?: string; highlight?: boolean; children: React.ReactNode }) {
  return (
    <div className={`grid items-center border-b border-[#f0f0f0] px-4 py-2.5 text-xs ${highlight ? 'bg-violet-50/70' : ''}`}
      style={{ gridTemplateColumns: grid ?? 'repeat(5, 1fr)' }}>
      {children}
    </div>
  );
}

function Av({ name, color = '#8E86FF' }: { name: string; color?: string }) {
  return (
    <span className="mr-1.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[9px] font-bold text-white"
      style={{ background: color }}>
      {name[0]}
    </span>
  );
}

function PageHdr({ title, sub, children }: { title: string; sub?: string; children?: React.ReactNode }) {
  return (
    <div className="flex shrink-0 items-center justify-between border-b border-[#e5e5e5] px-4 py-2.5">
      <div>
        <h2 className="text-sm font-bold text-zinc-900">{title}</h2>
        {sub && <p className="text-[10px] text-zinc-400">{sub}</p>}
      </div>
      {children && <div className="flex items-center gap-1.5">{children}</div>}
    </div>
  );
}

function Btn({ children, primary }: { children: React.ReactNode; primary?: boolean }) {
  return (
    <button type="button"
      className={`inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold ${primary ? 'bg-[#8E86FF] text-white' : 'border border-[#e5e5e5] bg-white text-zinc-600'}`}>
      {children}
    </button>
  );
}

function PaginationBar({ info }: { info: string }) {
  return (
    <div className="flex shrink-0 items-center justify-between border-t border-[#e5e5e5] bg-[#fafafa] px-4 py-1.5">
      <span className="text-[10px] text-zinc-400">{info}</span>
      <div className="flex gap-1">
        <span className="rounded border border-[#e5e5e5] bg-white px-2 py-0.5 text-[10px] text-zinc-500">Prev</span>
        <span className="rounded border border-[#e5e5e5] bg-white px-2 py-0.5 text-[10px] text-zinc-500">Next</span>
      </div>
    </div>
  );
}

function ScoreBar({ label, score, required, warn }: { label: string; score: number; required: number; warn?: boolean }) {
  return (
    <div className="mb-2 flex items-center gap-2">
      <span className="w-36 shrink-0 text-[10px] text-zinc-600">{label}</span>
      <div className="relative h-2 flex-1 rounded-full bg-zinc-100">
        <div className={`absolute left-0 top-0 h-2 rounded-full ${warn ? 'bg-amber-400' : 'bg-[#8E86FF]'}`} style={{ width: `${score}%` }} />
        <div className="absolute top-[-3px] h-4 w-0.5 bg-zinc-400" style={{ left: `${required}%` }} />
      </div>
      <span className={`w-7 text-right text-[10px] font-semibold ${warn ? 'text-amber-600' : 'text-zinc-700'}`}>{score}</span>
      {warn ? <AlertTriangle className="h-3 w-3 shrink-0 text-amber-500" /> : <Check className="h-3 w-3 shrink-0 text-emerald-500" />}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 0 — CANDIDATES  →  Candidate detail (Manage rights tab)
   List cursor: row-3 name col  (y≈44%, x≈34%)
   Detail cursor: 6th tab "Manage rights" (y≈17%, x≈60%)
══════════════════════════════════════════════════════════════════════════════ */

function CandidatesListFrame() {
  const rows = [
    { name: 'Sarah Chen',          role: 'Product Designer',  src: 'LinkedIn',  stage: 'Screening', score: 94, c: '#7B6EE8' },
    { name: 'Mark Lee',            role: 'SRE',               src: 'Referral',  stage: 'Applied',   score: 78, c: '#22c55e' },
    { name: 'Priya Krishnamurthy', role: 'TA Lead',           src: 'Indeed',    stage: 'Interview', score: 88, c: '#f97316' },
    { name: 'James Wilson',        role: 'Data Analyst',      src: 'Direct',    stage: 'Offer',     score: 91, c: '#3b82f6' },
    { name: 'Liu Wei',             role: 'Backend Engineer',  src: 'Agency',    stage: 'Screening', score: 82, c: '#a855f7' },
    { name: 'Riya Desai',          role: 'HR Manager',        src: 'LinkedIn',  stage: 'Applied',   score: 76, c: '#0ea5e9' },
    { name: 'Tom Bradley',         role: 'DevOps Engineer',   src: 'Referral',  stage: 'Applied',   score: 69, c: '#6366f1' },
    { name: 'Ana Ferreira',        role: 'UX Designer',       src: 'Indeed',    stage: 'Screening', score: 85, c: '#d946ef' },
    { name: 'Vikram Nair',         role: 'ML Engineer',       src: 'Direct',    stage: 'Applied',   score: 73, c: '#14b8a6' },
  ];
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Candidates" sub="41 candidates · AI-matched">
        <Btn><Download className="h-3 w-3" />Export</Btn>
        <Btn primary><Plus className="h-3 w-3" />Add</Btn>
      </PageHdr>
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] bg-white px-4 py-1.5">
        <span className="rounded-md bg-[rgba(142,134,255,0.12)] px-2 py-1 text-[10px] font-semibold text-[#7B6EE8]">Manage rights</span>
        <span className="rounded-md border border-[#e5e5e5] px-2 py-1 text-[10px] text-zinc-500">Filter</span>
        <span className="rounded-md border border-[#e5e5e5] px-2 py-1 text-[10px] text-zinc-500">Columns</span>
        <span className="ml-auto text-[10px] text-zinc-400">Auto tour — click any row</span>
      </div>
      <THead cols={['NAME', 'ROLE', 'SOURCE', 'STAGE', 'MATCH']} grid="2fr 2fr 1fr 1fr 1fr" />
      <div className="flex-1 overflow-hidden">
        {rows.map((r, i) => (
          <TRow key={r.name} grid="2fr 2fr 1fr 1fr 1fr" highlight={i === 2}>
            <span className="flex items-center text-xs font-medium text-zinc-800"><Av name={r.name} color={r.c} />{r.name}</span>
            <span className="text-[11px] text-zinc-600">{r.role}</span>
            <span className="text-[10px] text-zinc-400">{r.src}</span>
            <StagePill s={r.stage} />
            <Score v={r.score} />
          </TRow>
        ))}
      </div>
      <PaginationBar info="Showing 1–9 of 41" />
    </div>
  );
}

function CandidateDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      {/* Profile header */}
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <Av name="Priya Krishnamurthy" color="#f97316" />
        <div>
          <span className="text-sm font-bold text-zinc-900">Priya Krishnamurthy</span>
          <span className="ml-2 text-[11px] text-zinc-400">TA Lead · Globex · 7 yrs exp · Mumbai</span>
        </div>
        <Score v={88} />
      </div>
      {/* Tab bar — 6 tabs, "Manage rights" active */}
      <div className="flex shrink-0 items-center gap-0 border-b border-[#e5e5e5] bg-white px-4">
        {['Overview', 'Resume', 'AI Score', 'Interviews', 'Activity', 'Manage rights'].map((t, i) => (
          <button key={t} type="button"
            className={`whitespace-nowrap px-3 py-2 text-[11px] font-medium border-b-2 ${i === 5 ? 'border-[#8E86FF] text-[#7B6EE8]' : 'border-transparent text-zinc-500'}`}>
            {t}
          </button>
        ))}
      </div>
      {/* Manage rights content — fills remaining height */}
      <div className="flex flex-1 overflow-hidden gap-4 px-4 py-3">
        <div className="flex-1">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Workspace access</p>
          {[
            { f: 'View Candidates',     on: true  },
            { f: 'Edit Candidates',     on: true  },
            { f: 'Manage Jobs',         on: false },
            { f: 'Run AI Interviews',   on: false },
            { f: 'Access Reports',      on: true  },
            { f: 'Client Portal',       on: false },
          ].map(({ f, on }) => (
            <div key={f} className="flex items-center justify-between border-b border-[#f0f0f0] py-2">
              <span className="text-[11px] text-zinc-700">{f}</span>
              {on ? <ToggleRight className="h-4 w-4 text-[#8E86FF]" /> : <ToggleLeft className="h-4 w-4 text-zinc-300" />}
            </div>
          ))}
        </div>
        <div className="flex w-36 shrink-0 flex-col items-center justify-start rounded-xl border border-[#e5e5e5] bg-[#fafafa] p-3 text-center">
          <div className="mb-1 text-[10px] text-zinc-400">Role</div>
          <div className="text-sm font-bold text-zinc-800">Recruiter</div>
          <div className="mt-2 text-[10px] text-zinc-400">Scope</div>
          <div className="text-xs font-semibold text-violet-600">View · Edit</div>
          <button type="button" className="mt-3 w-full rounded-lg bg-[#8E86FF] px-2 py-1.5 text-[10px] font-bold text-white">
            Save changes
          </button>
          <p className="mt-2 text-[9px] text-zinc-400">Last changed 3d ago</p>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 1 — APPLICATIONS (KANBAN)  →  Application detail (Role-Fit)
   List cursor: Interview col card  (y≈31%, x≈61%)
   Detail cursor: 88% badge right panel  (y≈38%, x≈82%)
══════════════════════════════════════════════════════════════════════════════ */

function ApplicationsListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Applications" sub="Pipeline · 25 active applications">
        <Btn>Filter</Btn><Btn>Sort</Btn>
      </PageHdr>
      <div className="flex flex-1 gap-3 overflow-hidden px-4 py-3">
        {[
          { stage: 'Screening', color: 'bg-blue-100 text-blue-700', n: 8, cards: [
            { name: 'Liu Wei',    role: 'Backend Eng',    score: 82 },
            { name: 'Mark Lee',   role: 'SRE',            score: 78 },
            { name: 'Riya Desai', role: 'HR Manager',     score: 76 },
          ]},
          { stage: 'Interview', color: 'bg-violet-100 text-violet-700', n: 5, cards: [
            { name: 'Priya K.',   role: 'TA Lead',        score: 88 },
            { name: 'Sarah C.',   role: 'Product Design', score: 94 },
            { name: 'Ana F.',     role: 'UX Designer',    score: 85 },
          ]},
          { stage: 'Offer', color: 'bg-emerald-100 text-emerald-700', n: 2, cards: [
            { name: 'J. Wilson',  role: 'Data Analyst',   score: 91 },
            { name: 'Vikram N.',  role: 'ML Engineer',    score: 73 },
          ]},
        ].map(({ stage, color, n, cards }) => (
          <div key={stage} className="flex flex-1 flex-col rounded-xl border border-[#e5e5e5] bg-[#fafafa]">
            <div className="flex shrink-0 items-center justify-between border-b border-[#e5e5e5] px-3 py-2">
              <span className={`rounded-md px-2 py-0.5 text-[10px] font-bold ${color}`}>{stage}</span>
              <span className="text-[10px] text-zinc-400">{n}</span>
            </div>
            <div className="flex flex-1 flex-col gap-2 overflow-hidden p-2">
              {cards.map((c, ci) => (
                <div key={c.name}
                  className={`rounded-lg border px-2.5 py-2 ${ci === 0 && stage === 'Interview' ? 'border-violet-200 bg-violet-50' : 'border-[#e5e5e5] bg-white'}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-zinc-800">{c.name}</span>
                    <Score v={c.score} />
                  </div>
                  <p className="text-[10px] text-zinc-400">{c.role}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ApplicationDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <div>
          <span className="text-sm font-bold text-zinc-900">Priya K.</span>
          <span className="mx-2 text-[11px] text-zinc-400">→</span>
          <span className="text-sm font-semibold text-violet-700">TA Lead · Globex</span>
        </div>
        <span className="ml-auto text-[10px] text-zinc-400">Applied 2d ago</span>
      </div>
      {/* Stage progress */}
      <div className="flex shrink-0 items-center gap-0 border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-3">
        {['Applied', 'Screening', 'Interview', 'Offer'].map((s, i) => (
          <div key={s} className="flex items-center">
            <div className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-[10px] font-semibold ${i <= 2 ? 'bg-violet-100 text-violet-700' : 'bg-zinc-100 text-zinc-400'}`}>
              {i < 2 && <Check className="h-3 w-3" />}
              {i === 2 && <div className="h-2 w-2 rounded-full bg-violet-500" />}
              {s}
            </div>
            {i < 3 && <div className={`h-px w-6 ${i < 2 ? 'bg-violet-300' : 'bg-zinc-200'}`} />}
          </div>
        ))}
      </div>
      {/* Role-Fit detail — fills remaining height */}
      <div className="flex flex-1 overflow-hidden gap-4 px-4 py-3">
        <div className="flex-1">
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Role-Fit breakdown</p>
          <ScoreBar label="Communication"      score={92} required={80} />
          <ScoreBar label="Talent Acquisition" score={88} required={85} />
          <ScoreBar label="ATS Knowledge"      score={85} required={75} />
          <ScoreBar label="Stakeholder Mgmt"   score={72} required={80} warn />
          <ScoreBar label="Leadership"         score={70} required={80} warn />
          <p className="mt-3 text-[10px] text-zinc-400">3 strengths · 2 gaps · Recommend panel interview for leadership assessment</p>
        </div>
        <div className="flex w-32 shrink-0 flex-col items-center rounded-xl border border-[#e5e5e5] bg-[#fafafa] p-3 text-center">
          <div className="text-2xl font-extrabold text-[#8E86FF]">88%</div>
          <div className="text-[10px] text-zinc-400">Role-Fit</div>
          <div className="mt-2 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700">Strong fit</div>
          <div className="mt-3 w-full border-t border-[#e5e5e5] pt-2 text-[9px] text-zinc-400">Stage</div>
          <div className="text-xs font-bold text-violet-700">Interview</div>
          <button type="button" className="mt-2 w-full rounded-lg bg-[#8E86FF] px-2 py-1 text-[10px] font-bold text-white">Move to Offer</button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 2 — AI INTERVIEWS  →  Live session view
   List cursor: row-1 "Join Live" button  (y≈25%, x≈80%)
   Detail cursor: "Send Probe" button  (y≈49%, x≈80%)
══════════════════════════════════════════════════════════════════════════════ */

function AiInterviewsListFrame() {
  const rows = [
    { name: 'Sarah Chen',   role: 'Product Designer',  live: true,  dur: '04:23', status: 'In progress' },
    { name: 'Priya K.',     role: 'TA Lead',            live: false, dur: 'Scheduled', status: 'Tomorrow 10am' },
    { name: 'Mark Lee',     role: 'SRE',               live: false, dur: 'Scheduled', status: 'Tomorrow 2pm' },
    { name: 'James Wilson', role: 'Data Analyst',       live: false, dur: 'Scheduled', status: 'Thu 11am' },
    { name: 'Liu Wei',      role: 'Backend Engineer',   live: false, dur: 'Completed', status: 'Yesterday' },
    { name: 'Ana Ferreira', role: 'UX Designer',        live: false, dur: 'Completed', status: '2d ago' },
    { name: 'Tom Bradley',  role: 'DevOps Engineer',    live: false, dur: 'Completed', status: '3d ago' },
  ];
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="AI Interviews" sub="1 live · 3 upcoming · 3 completed">
        <Btn primary><Plus className="h-3 w-3" />Schedule</Btn>
      </PageHdr>
      <THead cols={['CANDIDATE', 'ROLE', 'STATUS', 'DURATION', 'ACTION']} />
      <div className="flex-1 overflow-hidden">
        {rows.map((r) => (
          <TRow key={r.name} highlight={r.live}>
            <span className="flex items-center text-xs font-medium text-zinc-800">
              <Av name={r.name} color={r.live ? '#ef4444' : '#8E86FF'} />{r.name}
            </span>
            <span className="text-[11px] text-zinc-600">{r.role}</span>
            {r.live
              ? <span className="flex items-center gap-1 text-[10px] font-bold text-red-600"><Radio className="h-3 w-3 animate-pulse" />LIVE</span>
              : <span className="text-[10px] text-zinc-400">{r.status}</span>}
            <span className="font-mono text-[11px] text-zinc-600">{r.dur}</span>
            {r.live
              ? <span className="inline-flex items-center rounded-md bg-[#8E86FF] px-2 py-0.5 text-[10px] font-bold text-white">Join Live</span>
              : <span className="text-[10px] text-zinc-400">—</span>}
          </TRow>
        ))}
      </div>
      <PaginationBar info="7 sessions · 1 live now" />
    </div>
  );
}

function AiInterviewDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      {/* LIVE bar */}
      <div className="flex shrink-0 items-center gap-3 border-b border-[#e5e5e5] bg-red-50 px-4 py-2">
        <Radio className="h-4 w-4 animate-pulse text-red-500" />
        <span className="text-xs font-bold text-red-600">LIVE · 04:23</span>
        <span className="text-xs text-zinc-600">Sarah Chen · Product Designer</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="rounded-md bg-white px-2 py-1 text-[10px] font-medium text-zinc-600 shadow-sm ring-1 ring-[#e5e5e5]">Mute</span>
          <span className="rounded-md bg-red-500 px-2 py-1 text-[10px] font-bold text-white">End</span>
        </div>
      </div>
      {/* Current question */}
      <div className="shrink-0 border-b border-[#e5e5e5] bg-white px-4 py-3">
        <p className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Current question (3 of 8)</p>
        <p className="text-sm font-semibold text-zinc-800">"Describe a time you improved a hiring funnel. What metrics did you use to measure success?"</p>
      </div>
      {/* AI Agent probe */}
      <div className="flex shrink-0 items-start gap-3 border-b border-[#e5e5e5] bg-violet-50 px-4 py-2.5">
        <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#8E86FF] text-[9px] font-bold text-white">AI</div>
        <div className="flex-1">
          <p className="text-[10px] font-semibold text-violet-700">Agent probe suggestion</p>
          <p className="text-[11px] text-zinc-700">"Follow up: Ask about the specific ATS tools used and the size of the team managed."</p>
        </div>
        <button type="button" className="shrink-0 rounded-lg bg-[#8E86FF] px-2.5 py-1.5 text-[10px] font-bold text-white">Send Probe</button>
      </div>
      {/* Transcript — fills remaining height */}
      <div className="flex-1 overflow-hidden px-4 py-2.5">
        <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">Live transcript</p>
        <p className="text-[11px] leading-relaxed text-zinc-600">
          <span className="font-semibold text-zinc-800">Sarah:</span> "In my last role at Northwind I reduced time-to-hire from 28 days to 14 by restructuring the screening workflow. I introduced async video screening which let us process 3× more candidates per week…"
        </p>
        <p className="mt-2 text-[11px] leading-relaxed text-zinc-600">
          <span className="font-semibold text-zinc-800">AI:</span> "That's impressive. What ATS system were you using and how did you measure quality-of-hire?"
        </p>
        <div className="mt-2 flex items-center gap-2">
          <div className="h-1.5 flex-1 rounded-full bg-violet-100">
            <div className="h-1.5 w-[55%] rounded-full bg-[#8E86FF]" />
          </div>
          <span className="text-[9px] text-zinc-400">Q3 of 8</span>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 3 — PRECISION MATCH  →  Explainability breakdown
   List cursor: "94%" badge row-1 (y≈39%, x≈46%)
   Detail cursor: warning panel (y≈51%, x≈55%)
══════════════════════════════════════════════════════════════════════════════ */

function PrecisionMatchListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Precision match" sub="Product Designer — Northwind (Remote)">
        <Btn>Export</Btn>
      </PageHdr>
      {/* JD criteria bars */}
      <div className="shrink-0 border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-2.5">
        <p className="mb-1.5 text-[9px] font-semibold uppercase tracking-wider text-zinc-400">JD Criteria Weights</p>
        {[['UX Research', 90], ['Figma / Prototyping', 80], ['Cross-functional Leadership', 70]].map(([l, w]) => (
          <div key={l as string} className="mb-1.5 flex items-center gap-2">
            <span className="w-44 text-[10px] text-zinc-600">{l}</span>
            <div className="h-1.5 flex-1 rounded-full bg-zinc-200"><div className="h-1.5 rounded-full bg-[#8E86FF]" style={{ width: `${w}%` }} /></div>
            <span className="w-6 text-right text-[10px] font-semibold text-zinc-500">{w}</span>
          </div>
        ))}
      </div>
      <THead cols={['CANDIDATE', 'MATCH', 'STRENGTHS', 'GAPS', 'ACTION']} />
      <div className="flex-1 overflow-hidden">
        {[
          { name: 'Sarah Chen',  score: 94, str: 4, gaps: 0, c: '#7B6EE8' },
          { name: 'Priya K.',    score: 88, str: 3, gaps: 1, c: '#f97316' },
          { name: 'Ana Ferreira',score: 84, str: 3, gaps: 1, c: '#d946ef' },
          { name: 'Mark Lee',    score: 71, str: 2, gaps: 2, c: '#22c55e' },
          { name: 'Riya Desai',  score: 65, str: 1, gaps: 3, c: '#0ea5e9' },
          { name: 'Tom Bradley', score: 58, str: 1, gaps: 3, c: '#6366f1' },
          { name: 'Vikram Nair', score: 52, str: 0, gaps: 4, c: '#14b8a6' },
        ].map((r, i) => (
          <TRow key={r.name} grid="2fr 1fr 1fr 1fr 1fr" highlight={i === 0}>
            <span className="flex items-center text-xs font-medium text-zinc-800"><Av name={r.name} color={r.c} />{r.name}</span>
            <Score v={r.score} />
            <span className="text-[11px] font-semibold text-emerald-600">{r.str} ✓</span>
            <span className={`text-[11px] font-semibold ${r.gaps > 0 ? 'text-amber-600' : 'text-zinc-400'}`}>{r.gaps > 0 ? `${r.gaps} △` : '—'}</span>
            <span className="cursor-pointer text-[10px] text-violet-600 underline">View fit</span>
          </TRow>
        ))}
      </div>
      <PaginationBar info="7 candidates matched" />
    </div>
  );
}

function PrecisionMatchDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <Av name="Sarah Chen" color="#7B6EE8" />
        <span className="text-sm font-bold text-zinc-900">Sarah Chen</span>
        <span className="mx-1 text-zinc-400">vs</span>
        <span className="text-sm text-zinc-600">Product Designer · Northwind</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-2xl font-extrabold text-[#8E86FF]">94%</span>
          <span className="text-[10px] text-zinc-400">match</span>
        </div>
      </div>
      <div className="flex-1 overflow-hidden px-4 py-3">
        <p className="mb-3 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
          Criteria breakdown &nbsp;(| = required threshold)
        </p>
        <ScoreBar label="UX Research"          score={95} required={90} />
        <ScoreBar label="Figma / Prototyping"  score={88} required={80} />
        <ScoreBar label="Cross-fn Leadership"  score={85} required={70} />
        <ScoreBar label="Stakeholder Mgmt"     score={65} required={75} warn />
        <ScoreBar label="Presentation Skills"  score={90} required={65} />
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5">
          <p className="text-[10px] font-semibold text-amber-700">△ Stakeholder Mgmt below required threshold (65 vs 75)</p>
          <p className="mt-0.5 text-[10px] text-amber-600">Recommendation: include a structured panel interview question focused on stakeholder communication and conflict resolution.</p>
        </div>
        <div className="mt-2 flex gap-4">
          <div className="flex-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-center">
            <p className="text-[9px] font-semibold uppercase text-emerald-600">Strengths</p>
            <p className="text-lg font-extrabold text-emerald-700">4</p>
          </div>
          <div className="flex-1 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-center">
            <p className="text-[9px] font-semibold uppercase text-amber-600">Gaps</p>
            <p className="text-lg font-extrabold text-amber-700">1</p>
          </div>
          <div className="flex-1 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-center">
            <p className="text-[9px] font-semibold uppercase text-violet-600">Overall</p>
            <p className="text-lg font-extrabold text-violet-700">94%</p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 4 — CLIENTS  →  Client workspace
   List cursor: Acme Bank name  (y≈25%, x≈35%)
   Detail cursor: Staff Engineer row  (y≈36%, x≈42%)
══════════════════════════════════════════════════════════════════════════════ */

function ClientsListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Clients" sub="Agency accounts · 7 active">
        <Btn primary><Plus className="h-3 w-3" />Add client</Btn>
      </PageHdr>
      <THead cols={['COMPANY', 'PLAN', 'JOBS', 'PIPELINE', 'ARR', 'STATUS']} grid="2fr 1fr 1fr 1fr 1fr 1fr" />
      <div className="flex-1 overflow-hidden">
        {[
          { n: 'Acme Bank',      plan: 'Pro',     jobs: 5,  pipe: 12, arr: '$24K', ok: true,  c: '#3b82f6' },
          { n: 'Northwind',      plan: 'Growth',  jobs: 3,  pipe:  8, arr: '$18K', ok: true,  c: '#22c55e' },
          { n: 'Contoso Ltd',    plan: 'Starter', jobs: 1,  pipe:  4, arr: '$9K',  ok: false, c: '#a855f7' },
          { n: 'Globex Corp',    plan: 'Pro',     jobs: 4,  pipe: 11, arr: '$21K', ok: true,  c: '#f97316' },
          { n: 'Initech',        plan: 'Growth',  jobs: 2,  pipe:  6, arr: '$12K', ok: true,  c: '#14b8a6' },
          { n: 'Vandelay Ind.',  plan: 'Starter', jobs: 1,  pipe:  2, arr: '$6K',  ok: true,  c: '#6366f1' },
          { n: 'Umbrella Corp',  plan: 'Pro',     jobs: 6,  pipe: 14, arr: '$28K', ok: true,  c: '#d946ef' },
        ].map((r, i) => (
          <TRow key={r.n} grid="2fr 1fr 1fr 1fr 1fr 1fr" highlight={i === 0}>
            <span className="flex items-center text-xs font-semibold text-zinc-800"><Av name={r.n} color={r.c} />{r.n}</span>
            <span className="text-[10px] text-zinc-500">{r.plan}</span>
            <span className="text-[11px] font-semibold text-zinc-800">{r.jobs}</span>
            <span className="text-[11px] text-zinc-600">{r.pipe}</span>
            <span className="text-[11px] font-semibold text-emerald-600">{r.arr}</span>
            <span className={`text-[10px] font-semibold ${r.ok ? 'text-emerald-600' : 'text-zinc-400'}`}>{r.ok ? 'Active' : 'Inactive'}</span>
          </TRow>
        ))}
      </div>
      <PaginationBar info="Showing 1–7 of 7 clients" />
    </div>
  );
}

function ClientDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-3 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <Av name="Acme Bank" color="#3b82f6" />
        <span className="text-sm font-bold text-zinc-900">Acme Bank</span>
        <span className="rounded-md bg-blue-50 px-2 py-0.5 text-[10px] font-semibold text-blue-700">Pro Plan</span>
        <span className="ml-auto text-[10px] text-zinc-400">Since Jan 2025</span>
      </div>
      {/* Stats row */}
      <div className="flex shrink-0 gap-3 border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-2.5">
        {[
          { label: 'Active Jobs',  value: '5',   Icon: Briefcase },
          { label: 'In Pipeline',  value: '12',  Icon: GitMerge },
          { label: 'ARR',          value: '$24K', Icon: TrendingUp },
          { label: 'Team',         value: '3',   Icon: Users },
        ].map(({ label, value, Icon }) => (
          <div key={label} className="flex flex-1 flex-col items-center rounded-lg border border-[#e5e5e5] bg-white py-2">
            <Icon className="mb-1 h-3.5 w-3.5 text-violet-500" />
            <span className="text-sm font-extrabold text-zinc-900">{value}</span>
            <span className="text-[9px] text-zinc-400">{label}</span>
          </div>
        ))}
      </div>
      {/* Job list */}
      <THead cols={['JOB TITLE', 'LOCATION', 'PIPELINE', 'STATE']} grid="2fr 1fr 1fr 1fr" />
      <div className="flex-1 overflow-hidden">
        {[
          { title: 'Staff Engineer',     loc: 'Mumbai',     pipe: 12, state: 'Open' },
          { title: 'Product Designer',   loc: 'Remote',     pipe:  8, state: 'Open' },
          { title: 'Data Analyst',       loc: 'Bengaluru',  pipe:  3, state: 'Hold' },
          { title: 'SRE Lead',           loc: 'Hybrid',     pipe:  5, state: 'Open' },
          { title: 'TA Manager',         loc: 'Mumbai',     pipe:  4, state: 'Open' },
        ].map((j, i) => (
          <TRow key={j.title} grid="2fr 1fr 1fr 1fr" highlight={i === 0}>
            <span className="text-xs font-medium text-violet-700">{j.title}</span>
            <span className="text-[11px] text-zinc-500">{j.loc}</span>
            <span className="text-[11px] font-semibold text-zinc-800">{j.pipe}</span>
            <span className={`text-[10px] font-semibold ${j.state === 'Open' ? 'text-emerald-600' : 'text-amber-600'}`}>{j.state}</span>
          </TRow>
        ))}
      </div>
      <PaginationBar info="5 active jobs for Acme Bank" />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 5 — RBAC  →  Permission editor
   List cursor: Edit icon row-2 (y≈31%, x≈82%)
   Detail cursor: AI Interviews toggle row-3 (y≈38%, x≈80%)
══════════════════════════════════════════════════════════════════════════════ */

function RbacListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Manage rights" sub="Team workspace access control">
        <Btn primary><Plus className="h-3 w-3" />Invite</Btn>
      </PageHdr>
      <THead cols={['MEMBER', 'ROLE', 'SCOPE', 'LAST ACTIVE', 'EDIT']} grid="2fr 1fr 2fr 1fr 1fr" />
      <div className="flex-1 overflow-hidden">
        {[
          { n: 'Neha Sharma',  role: 'Admin',     scope: 'Full access',  last: '2 min ago',   c: '#7B6EE8' },
          { n: 'Maya Patel',   role: 'Recruiter', scope: 'Limited',      last: '1h ago',      c: '#22c55e' },
          { n: 'Arjun Singh',  role: 'Viewer',    scope: 'View only',    last: '3h ago',      c: '#f97316' },
          { n: 'Priya R.',     role: 'Recruiter', scope: 'Limited',      last: '5h ago',      c: '#3b82f6' },
          { n: 'Sam Carter',   role: 'Viewer',    scope: 'View only',    last: '1d ago',      c: '#a855f7' },
          { n: 'Li Yan',       role: 'Recruiter', scope: 'Limited',      last: '2d ago',      c: '#14b8a6' },
        ].map((r, i) => (
          <TRow key={r.n} grid="2fr 1fr 2fr 1fr 1fr" highlight={i === 1}>
            <span className="flex items-center text-xs font-medium text-zinc-800"><Av name={r.n} color={r.c} />{r.n}</span>
            <span className="text-[11px] text-zinc-600">{r.role}</span>
            <span className="text-[11px] text-zinc-500">{r.scope}</span>
            <span className="text-[10px] text-zinc-400">{r.last}</span>
            <Edit2 className={`h-3.5 w-3.5 ${i === 1 ? 'text-violet-600' : 'text-zinc-400'}`} />
          </TRow>
        ))}
      </div>
      <PaginationBar info="6 team members" />
    </div>
  );
}

function RbacDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <Av name="Maya Patel" color="#22c55e" />
        <span className="text-sm font-bold text-zinc-900">Maya Patel</span>
        <span className="text-[11px] text-zinc-400">· Recruiter</span>
        <button type="button" className="ml-auto rounded-lg bg-[#8E86FF] px-3 py-1 text-[10px] font-bold text-white">Save changes</button>
      </div>
      <div className="flex-1 overflow-hidden px-4 py-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Feature access</p>
        {[
          { f: 'Candidates',    on: true,  scope: 'View · Edit' },
          { f: 'Jobs',          on: true,  scope: 'View' },
          { f: 'AI Interviews', on: false, scope: 'Off' },
          { f: 'Analytics',     on: false, scope: 'Off' },
          { f: 'Client Portal', on: false, scope: 'Off' },
          { f: 'Workflows',     on: false, scope: 'Off' },
        ].map(({ f, on, scope }) => (
          <div key={f}
            className={`mb-1.5 flex items-center justify-between rounded-lg border px-3 py-2.5 ${f === 'AI Interviews' ? 'border-violet-200 bg-violet-50' : 'border-[#e5e5e5] bg-white'}`}>
            <div>
              <span className="text-[11px] font-semibold text-zinc-800">{f}</span>
              <span className="ml-2 text-[10px] text-zinc-400">{scope}</span>
            </div>
            {on
              ? <ToggleRight className="h-5 w-5 text-[#8E86FF]" />
              : <ToggleLeft className={`h-5 w-5 ${f === 'AI Interviews' ? 'text-violet-300' : 'text-zinc-300'}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 6 — WORKFLOWS  →  Visual node canvas
   List cursor: row-1 name  (y≈25%, x≈34%)
   Detail cursor: "Send Email" node  (y≈51%, x≈83%)
══════════════════════════════════════════════════════════════════════════════ */

function WorkflowsListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Workflows" sub="Automation engine · 3 active">
        <Btn primary><Plus className="h-3 w-3" />New</Btn>
      </PageHdr>
      <THead cols={['NAME', 'TRIGGER', 'STATUS', 'RUNS', 'LAST RUN']} grid="2fr 2fr 1fr 1fr 1fr" />
      <div className="flex-1 overflow-hidden">
        {[
          { n: 'Interview Reminder',     t: 'Application submitted', status: 'Active', runs: 245, last: '10 min ago' },
          { n: 'Rejection Email',        t: 'Stage: Rejected',       status: 'Active', runs:  89, last: '2h ago' },
          { n: 'Offer Letter Sequence',  t: 'Stage: Offer',          status: 'Active', runs:  34, last: '1d ago' },
          { n: 'Screening Follow-up',    t: 'No response 48h',       status: 'Active', runs: 112, last: '30 min ago' },
          { n: 'Welcome Email',          t: 'Candidate added',       status: 'Paused', runs:  18, last: '5d ago' },
          { n: 'Interview Prep Kit',     t: 'Interview scheduled',   status: 'Active', runs:  67, last: '4h ago' },
          { n: 'Feedback Request',       t: 'Interview completed',   status: 'Paused', runs:  11, last: '1w ago' },
        ].map((r, i) => (
          <TRow key={r.n} grid="2fr 2fr 1fr 1fr 1fr" highlight={i === 0}>
            <span className="flex items-center gap-1.5 text-xs font-semibold text-violet-700">
              <Zap className="h-3 w-3 shrink-0" />{r.n}
            </span>
            <span className="text-[11px] text-zinc-500">{r.t}</span>
            <span className={`text-[10px] font-semibold ${r.status === 'Active' ? 'text-emerald-600' : 'text-amber-600'}`}>{r.status}</span>
            <span className="text-[11px] font-semibold text-zinc-700">{r.runs}</span>
            <span className="text-[10px] text-zinc-400">{r.last}</span>
          </TRow>
        ))}
      </div>
      <PaginationBar info="7 workflows · 5 active" />
    </div>
  );
}

function WorkflowDetailFrame() {
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <Zap className="h-4 w-4 text-violet-500" />
        <span className="text-sm font-bold text-zinc-900">Interview Reminder</span>
        <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-600">Active · 245 runs</span>
      </div>
      {/* Visual node canvas — fills remaining height */}
      <div className="flex flex-1 items-center justify-center gap-0 bg-[#fafafa] px-4 py-6">
        {[
          { label: 'Trigger',    detail: 'Application submitted',      cls: 'border-blue-300 bg-blue-50',    txt: 'text-blue-700' },
          { label: 'Filter',     detail: 'AI Score > 70%',             cls: 'border-violet-300 bg-violet-50', txt: 'text-violet-700' },
          { label: 'Wait',       detail: '2 hours',                    cls: 'border-zinc-300 bg-white',       txt: 'text-zinc-600' },
          { label: 'Send Email', detail: '"Interview Ready" template',  cls: 'border-emerald-300 bg-emerald-50 ring-2 ring-emerald-400 ring-offset-1', txt: 'text-emerald-700' },
        ].map(({ label, detail, cls, txt }, i) => (
          <div key={label} className="flex items-center">
            <div className={`w-36 rounded-xl border-2 p-3 shadow-sm ${cls}`}>
              <p className={`text-[9px] font-bold uppercase tracking-wider ${txt}`}>{label}</p>
              <p className={`mt-0.5 text-[10px] font-medium ${txt}`}>{detail}</p>
              {label === 'Send Email' && (
                <div className="mt-1.5 flex items-center gap-1 rounded-md bg-white/70 px-1.5 py-1">
                  <Mail className="h-2.5 w-2.5 text-emerald-600" />
                  <span className="text-[9px] text-emerald-600">View template</span>
                </div>
              )}
            </div>
            {i < 3 && (
              <div className="flex items-center">
                <div className="h-px w-5 bg-zinc-300" />
                <div className="h-0 w-0 border-y-[4px] border-l-[6px] border-y-transparent border-l-zinc-400" />
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="shrink-0 border-t border-[#e5e5e5] bg-white px-4 py-2">
        <p className="text-[10px] text-zinc-400">
          Runs when <strong className="text-zinc-700">application submitted</strong> → filters AI Score {'>'} 70% → waits 2h → sends personalised interview invite email.
        </p>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORY 7 — ANALYTICS  →  Time-to-hire trend
   List cursor: Time-to-Hire KPI card  (y≈23%, x≈32%)
   Detail cursor: Apr bar (last, rightmost)  (y≈77%, x≈83%)
══════════════════════════════════════════════════════════════════════════════ */

function AnalyticsListFrame() {
  return (
    <div className="flex h-full flex-col">
      <PageHdr title="Analytics" sub="Hiring insights · Last 6 months">
        <Btn><Download className="h-3 w-3" />Export</Btn>
      </PageHdr>
      {/* KPI cards */}
      <div className="flex shrink-0 gap-3 border-b border-[#e5e5e5] bg-[#fafafa] px-4 py-3">
        {[
          { label: 'Time-to-Hire',   value: '14d',  delta: '↓ 18%', highlight: true },
          { label: 'Fill Rate',      value: '78%',  delta: '↑ 5%',  highlight: false },
          { label: 'AI Acceptance',  value: '94%',  delta: '↑ 3%',  highlight: false },
          { label: 'Offer Decline',  value: '6%',   delta: '↓ 2%',  highlight: false },
        ].map(({ label, value, delta, highlight }) => (
          <div key={label} className={`flex flex-1 flex-col rounded-xl border bg-white p-2.5 ${highlight ? 'border-violet-300 ring-2 ring-violet-200 ring-offset-1' : 'border-[#e5e5e5]'}`}>
            <span className="text-[9px] font-semibold uppercase tracking-wider text-zinc-400">{label}</span>
            <span className="mt-0.5 text-xl font-extrabold text-zinc-900">{value}</span>
            <span className="text-[10px] font-semibold text-emerald-600">{delta}</span>
          </div>
        ))}
      </div>
      {/* Bar chart — fills remaining height */}
      <div className="flex-1 overflow-hidden px-4 py-3">
        <p className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Hires by source</p>
        {[
          { src: 'LinkedIn',  pct: 38, n: 22 },
          { src: 'Referral',  pct: 28, n: 16 },
          { src: 'Indeed',    pct: 18, n: 11 },
          { src: 'Agency',    pct: 10, n:  6 },
          { src: 'Direct',    pct:  6, n:  3 },
        ].map(({ src, pct, n }) => (
          <div key={src} className="mb-2 flex items-center gap-2">
            <span className="w-14 text-[10px] text-zinc-500">{src}</span>
            <div className="h-5 flex-1 rounded-md bg-zinc-100">
              <div className="h-5 rounded-md bg-[#8E86FF]" style={{ width: `${pct}%` }} />
            </div>
            <span className="w-4 text-right text-[10px] font-semibold text-zinc-600">{n}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AnalyticsDetailFrame() {
  const months = ['Nov', 'Dec', 'Jan', 'Feb', 'Mar', 'Apr'];
  const vals =   [22,    20,    18,    17,    15,    14];
  const maxV = 25;
  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-center gap-2 border-b border-[#e5e5e5] px-4 py-2.5">
        <ArrowLeft className="h-3.5 w-3.5 text-zinc-400" />
        <TrendingUp className="h-4 w-4 text-violet-500" />
        <span className="text-sm font-bold text-zinc-900">Time-to-Hire — 6 Month Trend</span>
        <span className="ml-auto rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold text-emerald-600">↓ 36% improvement</span>
      </div>
      {/* Bar chart — fills remaining height (flex-1) */}
      <div className="flex flex-1 items-end gap-3 px-6 pb-4 pt-3">
        {months.map((m, i) => {
          const h = Math.round((vals[i] / maxV) * 120);
          const isLast = i === months.length - 1;
          return (
            <div key={m} className="flex flex-1 flex-col items-center gap-1">
              <span className={`text-[10px] font-bold ${isLast ? 'text-violet-700' : 'text-zinc-500'}`}>{vals[i]}d</span>
              <div className={`w-full rounded-t-lg ${isLast ? 'bg-[#8E86FF] ring-2 ring-violet-400 ring-offset-1' : 'bg-violet-100'}`}
                style={{ height: `${h}px` }} />
              <span className={`text-[9px] ${isLast ? 'font-bold text-violet-700' : 'text-zinc-400'}`}>{m}</span>
            </div>
          );
        })}
      </div>
      {/* Breakdown — shrink-0 footer */}
      <div className="shrink-0 border-t border-[#e5e5e5] px-4 py-2">
        <div className="flex gap-8">
          {[['Engineering', '12d'], ['Design', '14d'], ['Operations', '18d']].map(([role, avg]) => (
            <div key={role}>
              <span className="text-[9px] text-zinc-400">{role}</span>
              <span className="ml-1 text-[11px] font-bold text-zinc-800">{avg}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   STORIES — cursor positions calculated from layout geometry
   Showcase: 1024 px wide × 550 px tall
   Sidebar:  220 px wide  (x=11% is sidebar item centre)
   Topbar:    ~40 px tall  (adds ~7% y-offset to frame content positions)
══════════════════════════════════════════════════════════════════════════════ */

const STORIES = [
  {
    id: 'candidates', crumbs: ['Data', 'Candidates'],
    /**
     * [0] sidebar Candidates  y=24% (52px logo+44px user+8px pad+18px label+13=135px → 135/550=24%)
     * [1] list row-3 highlight y=44% (40topbar+48hdr+32toolbar+30thead+2×40rows+20=248/550=45%)
     *     x=34% = name col centre (220+16+col1_half=220+16+110=346/1024=34%)
     * [2] detail "Manage rights" tab  y=17% (40+40hdr+18center=98/550=17.8%)
     *     x=60% = 6th tab centre (220+16+5tabs×~70px+5gaps+45=~630/1024=61%)
     */
    cursors: [{ x:11,y:24 }, { x:34,y:45 }, { x:60,y:17 }],
    ListFrame: CandidatesListFrame, DetailFrame: CandidateDetailFrame,
  },
  {
    id: 'applications', crumbs: ['Pipeline', 'Applications'],
    /**
     * [0] sidebar Applications  y=38%
     * [1] list Interview col first card  y=31% (40+48hdr+12py+36col-hdr+28card-ctr=164/550=30%)
     *     x=61% = Interview col centre (220+16+249+12+124=621/1024=61%)
     * [2] detail 88% right panel  y=38% (40+44+40+12+~50=186/550=34%) → use 36%
     *     x=82% = right panel x (~85% of showcase)
     */
    cursors: [{ x:11,y:38 }, { x:61,y:31 }, { x:82,y:36 }],
    ListFrame: ApplicationsListFrame, DetailFrame: ApplicationDetailFrame,
  },
  {
    id: 'aiInterviews', crumbs: ['Pipeline', 'AI Interviews'],
    /**
     * [0] sidebar AI Interviews  y=41%
     * [1] list "Join Live" row-1 last col  y=25% (40+50+30+18=138/550=25%)
     *     x=80% = last 1/5 col area (4/5 across content = 220+16+772×0.9=922/1024=90% → use 80%)
     * [2] detail "Send Probe" in probe section  y=49%
     *     (40+36live+60q+30probe_ctr=166+12pad_top=178/550=32%)  → probe section center
     *     Corrected: probe section starts at 40+36+60=136, center of probe(~50px): 136+25=161/550=29%
     *     Use y=29% but probe text and button visible → use y=49% for button area
     */
    cursors: [{ x:11,y:41 }, { x:80,y:25 }, { x:80,y:49 }],
    ListFrame: AiInterviewsListFrame, DetailFrame: AiInterviewDetailFrame,
  },
  {
    id: 'precisionMatch', crumbs: ['RoleFit AI', 'Precision match'],
    /**
     * [0] sidebar Precision match  y=51%
     * [1] list row-1 "94%" badge (MATCH col)  y=39% (40+48+80+30+18=216/550=39%)
     *     x=46% = MATCH col centre (220+16+154+77=467/1024=45.6%)
     * [2] detail warning panel  y=51% (40+44+12+3×30+8+30center=~230/550=42%) → use 51%
     *     x=55% = centre of warning panel
     */
    cursors: [{ x:11,y:51 }, { x:46,y:39 }, { x:55,y:51 }],
    ListFrame: PrecisionMatchListFrame, DetailFrame: PrecisionMatchDetailFrame,
  },
  {
    id: 'clients', crumbs: ['Data', 'Clients'],
    /**
     * [0] sidebar Clients  y=27%
     * [1] list Acme Bank row-1 name  y=25% (40+48+30+18=136/550=24.7%)
     *     x=35% = name col 1 centre (col1=2fr of 6fr, width=257px, centre=128px from content left)
     *     220+16+128=364/1024=35.5%
     * [2] detail Staff Engineer row-1  y=36% (40+44+64+30+18=196/550=35.6%)
     *     x=42% = name col centre in detail job list
     */
    cursors: [{ x:11,y:27 }, { x:35,y:25 }, { x:42,y:36 }],
    ListFrame: ClientsListFrame, DetailFrame: ClientDetailFrame,
  },
  {
    id: 'rbac', crumbs: ['Access', 'Manage rights'],
    /**
     * [0] sidebar Manage rights  y=59%
     * [1] list Edit icon row-2 (Maya Patel)  y=31% (40+48+30+40+18=176/550=32%)
     *     x=82% = last col (Edit icon, right side of content)
     * [2] detail AI Interviews toggle row-3  y=38% (40+44+12+20+2×38+19=211/550=38%)
     *     x=80% = right side where ToggleLeft/Right icon is
     */
    cursors: [{ x:11,y:59 }, { x:82,y:32 }, { x:80,y:38 }],
    ListFrame: RbacListFrame, DetailFrame: RbacDetailFrame,
  },
  {
    id: 'workflows', crumbs: ['Automations', 'Workflows'],
    /**
     * [0] sidebar Workflows  y=67%
     * [1] list row-1 "Interview Reminder" name  y=25% (40+50+30+18=138/550=25%)
     *     x=34% = name col centre (first 2fr of 7fr: width=220px, centre=110px → 220+16+110=346/1024=34%)
     * [2] detail "Send Email" node (4th, rightmost)  y=51% (canvas vertically centred)
     *     x=83% = 4th node centre (220+16+97+3×150+64=847/1024=83%)
     */
    cursors: [{ x:11,y:67 }, { x:34,y:25 }, { x:83,y:51 }],
    ListFrame: WorkflowsListFrame, DetailFrame: WorkflowDetailFrame,
  },
  {
    id: 'analytics', crumbs: ['Insights', 'Analytics'],
    /**
     * [0] sidebar Analytics  y=75%
     * [1] list Time-to-Hire KPI card (1st of 4)  y=23% (40+48+12+24=124/550=22.5%)
     *     x=32% = 1st card centre (card1 width=193px, centre=96px → 220+16+96=332/1024=32%)
     * [2] detail Apr bar (last/rightmost bar, highlighted)  y=77%
     *     Bar bottom at: 40+44+388px-16pb=456px. Apr bar height=67px. Centre=456-33=423/550=77%
     *     x=83% = 6th bar centre of 6 (220+24+5×128+64=952/1024≈83%)
     */
    cursors: [{ x:11,y:75 }, { x:32,y:23 }, { x:83,y:77 }],
    ListFrame: AnalyticsListFrame, DetailFrame: AnalyticsDetailFrame,
  },
] as const;

/* ─── timer constant ─────────────────────────────────────────────────────── */

/** Each of 3 phases lasts PHASE_MS.  Total per story = 3 × 1600 = 4800 ms. */
const PHASE_MS = 1600;

/* ─── main component ─────────────────────────────────────────────────────── */

export function FeaturesPageShowcase({
  heightClass = 'h-[460px] sm:h-[500px] md:h-[520px] lg:h-[550px]',
}: {
  heightClass?: string;
}) {
  const stepRef  = useRef(-1);
  const schedRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clickRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [storyIdx, setStoryIdx] = useState(0);
  const [frame, setFrame]       = useState<'list' | 'detail'>('list');
  const [cursorPos, setCursorPos] = useState(STORIES[0].cursors[1]);
  const [clicking, setClicking]   = useState(false);
  const [ripple, setRipple]       = useState(false);

  useEffect(() => {
    const fireClick = () => {
      setClicking(true);
      setRipple(true);
      if (clickRef.current) clearTimeout(clickRef.current);
      clickRef.current = setTimeout(() => { setClicking(false); setRipple(false); }, 280);
    };

    const tick = () => {
      stepRef.current += 1;
      const phase  = stepRef.current % 3;
      const sIdx   = Math.floor(stepRef.current / 3) % STORIES.length;
      const story  = STORIES[sIdx];

      if (phase === 0) {
        /* Navigate sidebar: set new story, list frame, cursor to sidebar item */
        setStoryIdx(sIdx);
        setFrame('list');
        setCursorPos(story.cursors[0]);
        schedRef.current = setTimeout(fireClick, 500);
      } else if (phase === 1) {
        /* Click an element in the list frame → transition to detail */
        setCursorPos(story.cursors[1]);
        schedRef.current = setTimeout(() => {
          fireClick();
          setTimeout(() => setFrame('detail'), 200);
        }, 560);
      } else {
        /* Interact with a specific element in the detail frame */
        setCursorPos(story.cursors[2]);
        schedRef.current = setTimeout(fireClick, 560);
      }
    };

    tick(); // run immediately on mount
    const timer = setInterval(tick, PHASE_MS);
    return () => {
      clearInterval(timer);
      if (schedRef.current) clearTimeout(schedRef.current);
      if (clickRef.current) clearTimeout(clickRef.current);
    };
  }, []);

  const story = STORIES[storyIdx];
  const { ListFrame, DetailFrame } = story;

  return (
    <div className={`relative flex w-full overflow-hidden rounded-[1.25rem] border border-[#e5e5e5] bg-white shadow-[0_25px_80px_-12px_rgba(0,0,0,0.28)] sm:rounded-[1.75rem] md:rounded-[2rem] ${heightClass}`}>
      <Sidebar activeId={story.id} />

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Topbar crumbs={story.crumbs} />

        {/* Zoom-in/zoom-out transition between frames */}
        <div className="relative flex-1 overflow-hidden">
          <AnimatePresence mode="wait">
            <motion.div
              key={`${storyIdx}-${frame}`}
              initial={{ opacity: 0, scale: 0.93 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 1.05 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              style={{ transformOrigin: 'top center' }}
              className="absolute inset-0 overflow-hidden"
            >
              {frame === 'list' ? <ListFrame /> : <DetailFrame />}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <CursorOverlay pos={cursorPos} clicking={clicking} ripple={ripple} />
    </div>
  );
}
